export type { BaseButtonProps } from "./BaseDeviceProps";
import KnxProductData from "../KnxProductdata";
import TemplateTreeViewModel from "../modelTreeViews/TemplateTreeviewModel";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import BaseObject from "./BaseObject";

import { DeviceClass, DeviceClasses } from "./DeviceClasses";
import TemplateGroup from "./TemplateGroup";
import { copyAllProperties } from "./utils";

//////////////////////////////////////////////////
import Logger from "../Logger";
const logger = new Logger();
import ObjectFactory from "./ObjectFactory";
import BaseModelReference from "./BaseModelReference";
import { showToast } from "../components/Toasts";
import type Device from "./Device";
import Cabinet from "./Cabinet";
import type Button from "./Button";
import Actor from "./Actor";
import { ChannelType } from "./ChannelType";
import { createBaseDeviceProps, type BaseDeviceProps, type GenericProductType } from "./BaseDeviceProps";
import { genericFrontImage } from "./genericFrontImage";
ObjectFactory.registerClass("DeviceTemplate", (data?: any, parent?: BaseModel) => {
  return new DeviceTemplate(data, parent);
});

enum ChannelDetectionMode {}

type OrderInfo = {
  date: number;
  eanNumber: number;
  articleNo: string;
  shopId: string;
  imageLink: string;
  pageLink: string;
  variantName: any;
  variantDescription: any;
  price: string;
  availabilty: string;
};

//////////////////////////////////////////////////
export default class DeviceTemplate extends BaseModel {
  public get className(): string {
    return "DeviceTemplate"; // no itx
  }

  getParentCollectionName(): string | undefined {
    return "templates"; // no itx
  }

  classification: DeviceClass = DeviceClasses.Unknown;

  image = "";
  description = "";

  private _useGenericImage = false;
  get useGenericImage(): boolean {
    return this._useGenericImage;
  }
  set useGenericImage(value: boolean) {
    if (this._useGenericImage === value) return;
    this.beforeChange("useGenericImage");
    this._useGenericImage = value;
    this.changed("useGenericImage");
  }

  get isGeneric(): boolean {
    return this._genericParams !== undefined;
  }

  private _genericProductType: GenericProductType = "Button";
  get genericProductType(): GenericProductType {
    return this._genericProductType;
  }
  set genericProductType(value: GenericProductType) {
    if (this._genericProductType === value) return;
    this.beforeChange("genericProductType");
    this._genericProductType = value;
    this.syncGenericProductData();
    this.changed("genericProductType");
  }

  // Absent for catalog products; present for a generic product definition.
  private _genericParams: BaseDeviceProps | undefined;
  get genericParams(): BaseDeviceProps | undefined {
    return this._genericParams;
  }

  set genericParams(value: BaseDeviceProps | undefined) {
    if (this._genericParams === value) return;
    this.beforeChange("genericParams");
    this._genericParams = value == null ? undefined : createBaseDeviceProps(value);
    if (this._genericParams) this.syncGenericProductData();
    this.changed("genericParams");
  }

  private _baseProps: BaseDeviceProps | undefined;
  get baseProps(): BaseDeviceProps | undefined {
    return this._baseProps;
  }
  set baseProps(value: BaseDeviceProps | undefined) {
    if (this._baseProps === value) return;
    this.beforeChange("baseProps");
    this._baseProps = value == null ? undefined : createBaseDeviceProps(value);
    this.changed("baseProps");
  }

  /** Mirrors generic definitions into the metadata used by existing product views. */
  syncGenericProductData(): void {
    const params = this.genericParams;
    if (!params) return;
    const actor = params.actorBaseProps;
    const button = params.buttonBaseProps;
    const isActor = this.genericProductType === "Actor" || this.genericProductType === "DecentralActor";
    const channelType =
      isActor && actor && actor.deviceType in ChannelType
        ? (actor?.deviceType as unknown as ChannelType)
        : ChannelType.Generic;
    const info = this.knxProductData.productInfo;
    Object.assign(info, {
      ManufacturerName: "Generic",
      ProductName: this.name,
      DefaultProductName: this.name,
      ProductType: this.genericProductType,
      ChannelType: channelType,
      Type: this.genericProductType === "Button" ? button?.type : isActor ? actor?.deviceType : this.genericProductType,
      NumberOfChannels: this.genericProductType === "Button" ? button?.numberOfButtonRows : actor?.channels,
      ManuallyCreated: "true",
      Programmable: "false",
      HasApplicationProgram: "false",
      HasConfig: "false",
      NoChannels: "false",
    });
    this.knxProductData.config.channelType = channelType;
    this.classification =
      this.genericProductType === "Actor"
        ? DeviceClasses.Actor
        : this.genericProductType === "Button"
          ? DeviceClasses.Button
          : this.genericProductType === "Sensor"
            ? DeviceClasses.SensorNew
            : this.genericProductType === "DecentralActor"
              ? DeviceClasses.DecentralActor
              : DeviceClasses.Unknown;
    this.knxProductData.changed("productInfo");
  }

  static createGeneric(
    parent: TemplateGroup,
    params: BaseDeviceProps,
    name: string,
    productType: GenericProductType = "Button",
  ): DeviceTemplate {
    const template = new DeviceTemplate({ name }, parent);
    template.genericProductType = productType;
    template.genericParams = params;
    parent.templates.add(template);
    return template;
  }

  // -----------------------------------------------------------------------------
  // knxProductData
  // -----------------------------------------------------------------------------
  _knxProductData = new KnxProductData({}, this);
  get knxProductData(): KnxProductData {
    return this._knxProductData;
  }

  set knxProductData(newValue: KnxProductData) {
    if (this._knxProductData === newValue) return;
    this.beforeChange("knxProductData");
    this._knxProductData = newValue;
    this.changed("knxProductData");
  }

  // -----------------------------------------------------------------------------
  // orderInformation
  // -----------------------------------------------------------------------------
  _orderInformation: OrderInfo = {
    date: Date.now(),
    eanNumber: 0,
    articleNo: "",
    shopId: "",
    imageLink: "",
    pageLink: "",
    variantName: {},
    variantDescription: {},
    price: "",
    availabilty: "",
  };
  get orderInformation(): OrderInfo {
    return this._orderInformation;
  }

  set orderInformation(newValue: OrderInfo) {
    if (this._orderInformation === newValue) return;
    this.beforeChange("orderInformation");
    this._orderInformation = newValue;
    this.changed("orderInformation");
  }

  //
  //   mwe 15.1.2025
  //   Templates haben keine InstanceData
  //    die defaultInstanceData die auf dem Server gespeichert werden
  //    sind heutzutage in knxProductdata.config
  //
  //  knxInstanceData = new KnxInstanceData({}, this);

  // -----------------------------------------------------------------------------
  // mappingInfo
  // -----------------------------------------------------------------------------
  _mappingInfo: any;
  get mappingInfo(): any {
    return this._mappingInfo;
  }

  set mappingInfo(newValue: any) {
    if (this._mappingInfo === newValue) return;
    this.beforeChange("mappingInfo");
    this._mappingInfo = newValue;
    this.changed("mappingInfo");
  }

  // -----------------------------------------------------------------------------

  //
  // allowed types "TM" // Technische Handbuch
  //
  // manualUrls = new Map<string, string>();

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new TemplateTreeViewModel(this);
  }

  getPersistentProperties(): string[] {
    return [
      "type", // no itx
      "knxInstanceData", // no itx
      "oref:knxProductData", // no itx
      "mappingInfo", // no itx
      "orderInformation", // no itx
      "genericProductType",
      "baseProps",
      "genericParams", // no itx
      "useGenericImage",
      //      "manualUrls",
      ...super.getPersistentProperties(),
    ];
  }

  // non persistent dummy Device as helper Object
  dummyDevice?: Device;
  ///////////////////////////////

  delete(): Array<BaseObject> {
    this.isDeleting = true;
    state.allDevices().forEach((dev) => {
      if (dev.templateId == this.id) {
        dev.templateId = "";
      }
    });
    return super.delete();
  }

  get isActor(): boolean {
    if (this.genericParams) return this.genericProductType === "Actor";
    const templateGroup = this.parent as TemplateGroup;
    if (templateGroup) {
      return templateGroup.templateGroupType.name == DeviceClasses.Actor.name;
    }
    return true;
  }

  get isButton(): boolean {
    if (this.genericParams) return ["Button", "Sensor", "DecentralActor"].includes(this.genericProductType);
    const templateGroup = this.parent as TemplateGroup;
    if (templateGroup) {
      return (
        templateGroup.templateGroupType.name == DeviceClasses.SensorNew.name ||
        templateGroup.templateGroupType.name == DeviceClasses.DecentralActor.name ||
        templateGroup.templateGroupType.name == DeviceClasses.Button.name
      );
    }
    return false;
  }

  get isSensor(): boolean {
    if (this.genericParams) return this.genericProductType === "Sensor";
    const templateGroup = this.parent as TemplateGroup;
    if (templateGroup) {
      return templateGroup.templateGroupType.name == DeviceClasses.SensorNew.name;
    }
    return false;
  }

  get isDecentralActor(): boolean {
    if (this.genericParams) return this.genericProductType === "DecentralActor";
    const templateGroup = this.parent as TemplateGroup;
    if (templateGroup) {
      return templateGroup.templateGroupType.name == DeviceClasses.DecentralActor.name;
    }
    return false;
  }

  getUsages(): Array<BaseModelReference> {
    const res = new Array<BaseModelReference>();
    state.allDevices().forEach((device) => {
      if (device.template == this) {
        res.push(new BaseModelReference({ referencedObject: device }, this));
      }
    });

    return res.sort((a, b) => a.fullTag.localeCompare(b.fullTag));
  }

  static async createFromServerData(
    parent: TemplateGroup,
    modelDataId: string,
    metadata: any,
    articleNo?: string,
  ): Promise<DeviceTemplate> {
    let template = new DeviceTemplate({}, parent);
    parent.templates.add(template);

    const knxProductData = new KnxProductData({ modelDataId: modelDataId, productInfo: metadata }, template);

    template.knxProductData = knxProductData;

    template.parent = parent;
    template.classification = parent.templateGroupType;
    template = this.updateVariantData(template, metadata, articleNo);
    return template;
  }

  static updateVariantData(template: DeviceTemplate, metadata: any, articleNo?: string): DeviceTemplate {
    const productInfo = template?.knxProductData?.productInfo;
    const productName = productInfo?.ProductName;
    let manufactor = productInfo?.ManufacturerName;
    manufactor = manufactor ? manufactor.split(" ")[0] : ""; // "MDT Technologies" => "MDT"

    // Add Variant Data
    if (articleNo) {
      const variants = metadata?.Variants ? metadata?.Variants : [];
      const variant = variants?.findLast((item) => item.articleNo == articleNo);
      if (variant) {
        template.orderInformation.date = parseInt(variant.shopdata?.voltus?.date || "0");
        template.orderInformation.eanNumber = parseInt(variant.EAN) || 0;
        template.orderInformation.articleNo = variant.articleNo || "";
        template.orderInformation.shopId = variant.shopdata?.voltus?.id || "";
        template.orderInformation.imageLink = variant.shopdata?.voltus?.image || "";
        template.orderInformation.pageLink = variant.shopdata?.voltus?.page || "";
        template.orderInformation.price = variant.shopdata?.voltus?.price || "";
        template.orderInformation.availabilty = variant.shopdata?.voltus?.availabilty || "";
        template.orderInformation.variantName = {
          "de-DE": variant.description || "",
          "en-US": variant.description || "",
        };
        template.orderInformation.variantDescription = {
          "de-DE": "",
          "en-US": "",
        };
      }
    }

    let n = `${manufactor} ${template.orderInformation?.articleNo ?? metadata?.OrderNumber} ${template.orderInformation?.variantName?.["de-DE"] ?? productName}`;
    n = n.trim();
    if (n) {
      template.name = n;
    }

    return template;
  }

  //////////////////////////////
  canBeEditedInteractive(): boolean {
    return false;
  }

  canBeDeletedInteractive(): boolean {
    if (this.getUsages().length > 0) {
      return false;
    }
    return true;
  }
  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const p = [this.getUsages().length];
      return state.translate("DeviceTemplate.templateIsUsedT1Times", `Dieses Produkt wird T1 mal verwendet`, {
        T1: `${p[0]}`,
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }

  createDummyDevice() {
    if (!this.dummyDevice) {
      let cabinet = state.cabinets.sortBy("tag")[0];
      if (!cabinet) {
        cabinet = new Cabinet(
          {
            name: "dummy", // no itx
          },
          state.cabinets,
        );
      }
      if (this.isActor) {
        this.dummyDevice = ObjectFactory.createObject("Actor", {
          tag: "@A", // no itx
          name: this.name,
          parent: cabinet,
        }) as Actor;
      } else {
        this.dummyDevice = ObjectFactory.createObject("Button", {
          tag: "@T", // no itx
          name: this.name,
        }) as Button;
      }

      if (this.dummyDevice) {
        this.dummyDevice.templateId = this.id;

        this.dummyDevice.configTemplate = true;
      }
    }
  }

  //// for Paste

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;
    if (item.className == "DeviceTemplate") {
      if (this.parent) {
        return await item.executePaste({
          parent: this.parent as BaseModel,
          isCopyRootObj: true,
        });
      }
    }
    return [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    const undoObjects: Array<BaseModel> = [];
    // check if already in Group??
    if (!(parent as TemplateGroup)?.templateAlreadyInGroup(this)) {
      // set parent
      this.parent = parent;
      (parent as TemplateGroup).templates.add(this);
      undoObjects.push(this);
    } else {
      showToast({
        text: state.translate("DeviceTemplate.errorTemplateAlreadyInGroup", "Produkt schon in dieser Gruppe vorhanden"),
      });
    }
    return undoObjects;
  }

  //
  // this property returns the image source for the device template
  // the central product data is the only source of product images
  //
  // the image data has to be load async before !!!!
  //

  getFrontImage(device: Device | undefined): string | undefined {
    if (this.useGenericImage) {
      const params = device?.baseProps.buttonBaseProps ?? this.genericParams?.buttonBaseProps;
      return params ? genericFrontImage(params, device) : undefined;
    }
    if (this.genericParams?.buttonBaseProps && this.genericProductType === "Button") {
      return genericFrontImage(device?.baseProps.buttonBaseProps ?? this.baseProps?.buttonBaseProps ?? this.genericParams.buttonBaseProps, device);
    }
    if (this.knxProductData.frontImage) {
      return this.knxProductData.frontImage;
    }
    return undefined;
  }

  consistencyCheck(parent: BaseModel) {
    this.knxProductData.consistencyCheck(this);
  }
}
